import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { inventoryCorrectionsApi } from "../../../api/inventory_corrections";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../components/utils/ResourceError";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { PlusIcon, PencilIcon, TrashIcon, CheckCircleIcon, DocumentCheckIcon } from "@heroicons/react/24/outline";
import { Button } from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { TextField } from "@mui/material";
import toast from "react-hot-toast";

export const InventoryCorrections = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [corrections, setCorrections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newCorrection, setNewCorrection] = useState({
    title: "",
    description: "",
    correction_date: new Date(),
  });

  useEffect(() => {
    fetchCorrections();
  }, []);

  const fetchCorrections = async () => {
    try {
      setLoading(true);
      const response = await inventoryCorrectionsApi.getInventoryCorrections();
      if (response?.data?.success) {
        setCorrections(response.data.data);
      } else {
        setError("Kunne ikke hente lagerkorreksjoner");
      }
    } catch (err) {
      console.error("Error fetching corrections:", err);
      setError("Kunne ikke hente lagerkorreksjoner");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newCorrection.title.trim()) {
      toast.error("Tittel er påkrevd");
      return;
    }

    try {
      const response = await inventoryCorrectionsApi.createInventoryCorrection({
        title: newCorrection.title,
        description: newCorrection.description,
        correction_date: newCorrection.correction_date.toISOString().split("T")[0],
      });

      if (response?.data?.success) {
        setCreateDialogOpen(false);
        setNewCorrection({ title: "", description: "", correction_date: new Date() });
        fetchCorrections();
        navigate(`/dashboard/inventory/corrections/${response.data.data.id}`);
        toast.success("Lagerkorreksjon opprettet");
      } else {
        toast.error("Kunne ikke opprette lagerkorreksjon");
      }
    } catch (error) {
      console.error("Error creating correction:", error);
      toast.error("Kunne ikke opprette lagerkorreksjon");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "draft":
        return "bg-gray-100 text-gray-800";
      case "pending_approval":
        return "bg-yellow-100 text-yellow-800";
      case "approved":
        return "bg-green-100 text-green-800";
      case "rejected":
        return "bg-red-100 text-red-800";
      case "completed":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "draft":
        return "Utkast";
      case "pending_approval":
        return "Venter på godkjenning";
      case "approved":
        return "Godkjent";
      case "rejected":
        return "Avslått";
      case "completed":
        return "Fullført";
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <>
        <DashboardHeader title="Lagerkorreksjoner" subtitle="Laster..." />
        <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8">
          <ResourceLoading />
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <DashboardHeader title="Lagerkorreksjoner" subtitle="" />
        <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8">
          <ResourceError errors={[error]} />
        </div>
      </>
    );
  }

  return (
    <>
      <DashboardHeader
        title="Lagerkorreksjoner"
        subtitle="Administrer lagerkorreksjoner"
        actions={[
          {
            component: null,
            label: "Ny korreksjon",
            description: "",
            path: null,
            action: () => setCreateDialogOpen(true),
            icon: PlusIcon,
            textColor: "text-white",
            bgColor: "bg-blue-600",
            hidden: false,
            menu: true,
          },
        ]}
      />
      <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8 pb-24">
        <div className="mt-8">
          <div className="bg-white shadow rounded-lg overflow-hidden">
            {corrections.length > 0 ? (
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Korreksjonsnummer</TableCell>
                    <TableCell>Tittel</TableCell>
                    <TableCell>Dato</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Antall linjer</TableCell>
                    <TableCell>Total mengde</TableCell>
                    <TableCell align="right">Handlinger</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {corrections.map((correction) => (
                    <TableRow key={correction.id} hover>
                      <TableCell>{correction.correction_number || "-"}</TableCell>
                      <TableCell>{correction.title}</TableCell>
                      <TableCell>
                        {correction.correction_date
                          ? new Date(correction.correction_date).toLocaleDateString("nb-NO")
                          : "-"}
                      </TableCell>
                      <TableCell>
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                            correction.status
                          )}`}
                        >
                          {getStatusLabel(correction.status)}
                        </span>
                      </TableCell>
                      <TableCell>{correction.total_items || 0}</TableCell>
                      <TableCell>{correction.total_quantity || 0}</TableCell>
                      <TableCell align="right">
                        <button
                          onClick={() => navigate(`/dashboard/inventory/corrections/${correction.id}`)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Vis"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-12">
                <DocumentCheckIcon className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Ingen korreksjoner</h3>
                <p className="text-gray-500 mb-4">Opprett din første lagerkorreksjon for å komme i gang</p>
                <Button variant="contained" onClick={() => setCreateDialogOpen(true)}>
                  Opprett korreksjon
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create Dialog */}
      <Transition.Root show={createDialogOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setCreateDialogOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div>
                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900 mb-4">
                      Opprett ny lagerkorreksjon
                    </Dialog.Title>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Tittel *</label>
                        <TextField
                          fullWidth
                          variant="outlined"
                          value={newCorrection.title}
                          onChange={(e) => setNewCorrection({ ...newCorrection, title: e.target.value })}
                          placeholder="F.eks. Korreksjon januar 2024"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Beskrivelse</label>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          variant="outlined"
                          value={newCorrection.description}
                          onChange={(e) => setNewCorrection({ ...newCorrection, description: e.target.value })}
                          placeholder="Beskrivelse av korreksjonen..."
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Korreksjonsdato *</label>
                        <TextField
                          fullWidth
                          type="date"
                          variant="outlined"
                          value={
                            newCorrection.correction_date
                              ? new Date(newCorrection.correction_date).toISOString().split("T")[0]
                              : ""
                          }
                          onChange={(e) =>
                            setNewCorrection({
                              ...newCorrection,
                              correction_date: e.target.value ? new Date(e.target.value) : new Date(),
                            })
                          }
                          InputLabelProps={{
                            shrink: true,
                          }}
                        />
                      </div>

                      <div className="flex justify-end space-x-2 mt-4">
                        <Button variant="outlined" onClick={() => setCreateDialogOpen(false)}>
                          Avbryt
                        </Button>
                        <Button variant="contained" onClick={handleCreate}>
                          Opprett
                        </Button>
                      </div>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};

